import React, { useState, useEffect } from 'react';
import {
  CpuChipIcon,
  CodeBracketIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  WrenchScrewdriverIcon,
  BoltIcon,
  DocumentTextIcon,
  RectangleGroupIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Define valid exercise types
export type EngineeringTechExerciseType = 
  | 'circuit-design' 
  | 'algorithm-implementation' 
  | 'system-architecture' 
  | 'code-debugging' 
  | 'technical-specification';

// Helper function to check if exercise type is valid
export const isValidEngineeringTechExerciseType = (type: string): type is EngineeringTechExerciseType => {
  const validTypes: EngineeringTechExerciseType[] = [
    'circuit-design',
    'algorithm-implementation', 
    'system-architecture',
    'code-debugging',
    'technical-specification'
  ];
  return validTypes.includes(type as EngineeringTechExerciseType);
};

interface EngineeringTechInteractiveProps {
  content: {
    id: string;
    title: string;
    description: string;
    type: string;
    interactiveData: {
      exerciseType: EngineeringTechExerciseType;
      instructions: string;
      scenario?: any;
      expectedResults?: any;
      hints?: string[];
    };
  };
  onComplete: (completed: boolean, score?: number) => void;
}

const EngineeringTechInteractive: React.FC<EngineeringTechInteractiveProps> = ({
  content,
  onComplete
}) => {
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [results, setResults] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [currentHint, setCurrentHint] = useState(0);
  const [codeOutput, setCodeOutput] = useState('');
  const [circuitSimulation, setCircuitSimulation] = useState<any>(null);
  
  // Tracking states
  const [timeSpent, setTimeSpent] = useState(0);
  const [windowFocused, setWindowFocused] = useState(true);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [inputInteractions, setInputInteractions] = useState(0);
  const [minimumEngagementTime] = useState(240); // 4 minutes for engineering
  const [isEngaged, setIsEngaged] = useState(false);

  const { exerciseType, instructions, scenario, hints } = content.interactiveData;

  // Debug logging and validation for exercise type
  useEffect(() => {
    console.log('EngineeringTechInteractive Debug:', {
      exerciseType,
      contentType: content.type,
      interactiveData: content.interactiveData,
      isValidType: isValidEngineeringTechExerciseType(exerciseType || '')
    });

    if (exerciseType && !isValidEngineeringTechExerciseType(exerciseType)) {
      console.error(`Invalid engineering/tech exercise type: "${exerciseType}"`);
      toast.error(`Invalid exercise type: "${exerciseType}". Please check the configuration.`);
    }
  }, [exerciseType, content]);

  useEffect(() => {
    initializeExercise();
  }, [exerciseType]);

  // Window focus tracking
  useEffect(() => {
    if (isSubmitted) return;

    const handleFocus = () => setWindowFocused(true);
    const handleBlur = () => {
      setWindowFocused(false);
      setTabSwitches(prev => prev + 1);
      if (tabSwitches >= 2) {
        toast('⚠️ Stay focused on your engineering work for accurate assessment.', {
          icon: '⚙️',
          duration: 3000,
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWindowFocused(false);
      } else {
        setWindowFocused(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSubmitted, tabSwitches]);

  // Time tracking
  useEffect(() => {
    if (isSubmitted) return;

    const interval = setInterval(() => {
      if (windowFocused) {
        setTimeSpent(prev => {
          const newTime = prev + 1;
          if (newTime >= minimumEngagementTime && !isEngaged) {
            setIsEngaged(true);
          }
          return newTime;
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [windowFocused, isSubmitted, minimumEngagementTime, isEngaged]);

  const initializeExercise = () => {
    if (!exerciseType) {
      console.warn('EngineeringTechInteractive: No exerciseType provided');
      setInputs({});
      return;
    }

    switch (exerciseType) {
      case 'circuit-design':
        setInputs({
          components: [],
          connections: [],
          calculations: '',
          expectedBehavior: '',
          testProcedure: ''
        });
        break;
      case 'algorithm-implementation':
        setInputs({
          pseudocode: '',
          implementation: '',
          timeComplexity: '',
          spaceComplexity: '',
          testCases: [{ input: '', expectedOutput: '' }]
        });
        break;
      case 'system-architecture':
        setInputs({
          components: [],
          dataFlow: '',
          scalability: '',
          securityConsiderations: '',
          documentation: ''
        });
        break;
      case 'code-debugging':
        setInputs({
          bugIdentification: [],
          fixes: [],
          explanation: '',
          improvedCode: scenario?.buggyCode || ''
        });
        break;
      case 'technical-specification':
        setInputs({
          requirements: [''],
          constraints: [''],
          specifications: '',
          acceptance: [''],
          timeline: ''
        });
        break;
      default:
        console.warn(`EngineeringTechInteractive: Unknown exercise type "${exerciseType}"`);
        setInputs({});
        break;
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setInputInteractions(prev => prev + 1);
  };

  const runCodeTest = () => {
    // Simulate code execution
    try {
      const mockOutput = `Test Results:\n✓ Test case 1 passed\n✓ Test case 2 passed\n⚠ Test case 3: edge case detected\n\nExecution time: 0.23ms\nMemory usage: 1.2MB`;
      setCodeOutput(mockOutput);
      toast.success('Code test completed!');
    } catch (error) {
      setCodeOutput('Error: Compilation failed');
      toast.error('Code has syntax errors');
    }
  };

  const simulateCircuit = () => {
    // Mock circuit simulation
    const voltage = Math.random() * 12;
    const current = voltage / (inputs.resistance || 10);
    const power = voltage * current;
    
    setCircuitSimulation({
      voltage: voltage.toFixed(2),
      current: current.toFixed(3),
      power: power.toFixed(2),
      status: current < 1 ? 'Safe' : 'Warning: High Current'
    });
    
    toast.success('Circuit simulation complete!');
  };

  const validateSubmission = () => {
    if (!isEngaged) {
      toast.error(`Please spend at least ${Math.ceil(minimumEngagementTime / 60)} minutes working on the problem.`);
      return false;
    }

    const requiredFields = getRequiredFields();
    const missingFields = requiredFields.filter(field => {
      const value = inputs[field];
      return !value || (Array.isArray(value) ? value.every(v => !v) : !value.length);
    });
    
    if (missingFields.length > 0) {
      toast.error('Please complete all required fields before submitting.');
      return false;
    }

    return true;
  };

  const getRequiredFields = () => {
    switch (exerciseType) {
      case 'circuit-design':
        return ['expectedBehavior'];
      case 'algorithm-implementation':
        return ['implementation', 'timeComplexity'];
      case 'system-architecture':
        return ['dataFlow', 'documentation'];
      case 'code-debugging':
        return ['explanation', 'improvedCode'];
      case 'technical-specification':
        return ['specifications'];
      default:
        return [];
    }
  };

  const evaluateSubmission = () => {
    switch (exerciseType) {
      case 'circuit-design':
        return evaluateCircuitDesign();
      case 'algorithm-implementation':
        return evaluateAlgorithm();
      case 'system-architecture':
        return evaluateSystemArchitecture();
      case 'code-debugging':
        return evaluateDebugging();
      case 'technical-specification':
        return evaluateTechnicalSpec();
      default:
        return { score: 0, feedback: 'Unknown exercise type' };
    }
  };

  const evaluateCircuitDesign = () => {
    let score = 0;
    const feedback = [];

    // Component selection (25 points)
    const componentCount = inputs.components?.length || 0;
    if (componentCount >= 3) {
      score += 25;
      feedback.push('✓ Adequate components selected');
    } else {
      feedback.push('Include more circuit components');
    }

    // Calculations (30 points)
    if (inputs.calculations?.length > 50) {
      score += 30;
      feedback.push('✓ Circuit calculations provided');
    } else {
      feedback.push('Show Ohm\'s law and power calculations');
    }

    // Expected behavior (25 points)
    if (inputs.expectedBehavior?.length > 40) {
      score += 25;
      feedback.push('✓ Circuit behavior well described');
    }

    // Test procedure (20 points)
    if (inputs.testProcedure?.length > 30) {
      score += 20;
      feedback.push('✓ Testing procedure outlined');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateAlgorithm = () => {
    let score = 0;
    const feedback = [];

    // Implementation quality (40 points)
    if (inputs.implementation?.length > 100) {
      score += 40;
      feedback.push('✓ Algorithm implementation provided');
    }

    // Complexity analysis (30 points)
    if (inputs.timeComplexity && inputs.spaceComplexity) {
      score += 30;
      feedback.push('✓ Complexity analysis complete');
    } else {
      feedback.push('Include both time and space complexity');
    }

    // Test cases (20 points)
    const validTests = inputs.testCases?.filter((tc: any) => tc.input && tc.expectedOutput).length || 0;
    score += Math.min(20, validTests * 10);

    // Pseudocode (10 points)
    if (inputs.pseudocode?.length > 30) {
      score += 10;
      feedback.push('✓ Pseudocode provided');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateSystemArchitecture = () => {
    let score = 0;
    const feedback = [];

    // Components (20 points)
    if (inputs.components?.length >= 3) {
      score += 20;
      feedback.push('✓ System components identified');
    }

    // Data flow (30 points)
    if (inputs.dataFlow?.length > 60) {
      score += 30;
      feedback.push('✓ Data flow well described');
    }

    // Scalability (25 points)
    if (inputs.scalability?.length > 40) {
      score += 25;
      feedback.push('✓ Scalability considerations addressed');
    }

    // Security (15 points)
    if (inputs.securityConsiderations?.length > 30) {
      score += 15;
      feedback.push('✓ Security aspects considered');
    }

    // Documentation (10 points)
    if (inputs.documentation?.length > 50) {
      score += 10;
      feedback.push('✓ System documented');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateDebugging = () => {
    let score = 0;
    const feedback = [];

    // Bug identification (30 points)
    const bugsFound = inputs.bugIdentification?.filter((bug: string) => bug.length > 10).length || 0;
    score += Math.min(30, bugsFound * 10);
    if (bugsFound >= 2) {
      feedback.push('✓ Bugs correctly identified');
    }

    // Fixes (40 points)
    const validFixes = inputs.fixes?.filter((fix: string) => fix.length > 15).length || 0;
    score += Math.min(40, validFixes * 13.33);

    // Explanation (20 points)
    if (inputs.explanation?.length > 50) {
      score += 20;
      feedback.push('✓ Debugging process explained');
    }

    // Code improvement (10 points)
    if (inputs.improvedCode?.length > inputs.originalCode?.length) {
      score += 10;
      feedback.push('✓ Code improvements made');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateTechnicalSpec = () => {
    let score = 0;
    const feedback = [];

    // Requirements (25 points)
    const validReqs = inputs.requirements?.filter((req: string) => req.length > 20).length || 0;
    score += Math.min(25, validReqs * 8.33);

    // Constraints (20 points)
    const validConstraints = inputs.constraints?.filter((c: string) => c.length > 15).length || 0;
    score += Math.min(20, validConstraints * 10);

    // Specifications (35 points)
    if (inputs.specifications?.length > 100) {
      score += 35;
      feedback.push('✓ Comprehensive specifications');
    }

    // Acceptance criteria (15 points)
    const validAcceptance = inputs.acceptance?.filter((a: string) => a.length > 20).length || 0;
    score += Math.min(15, validAcceptance * 7.5);

    // Timeline (5 points)
    if (inputs.timeline?.length > 20) {
      score += 5;
      feedback.push('✓ Timeline provided');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const handleSubmit = () => {
    if (!validateSubmission()) return;

    const evaluation = evaluateSubmission();
    const finalScore = Math.max(0, Math.min(100, evaluation.score));
    const passed = finalScore >= 70;

    setResults({
      score: finalScore,
      feedback: evaluation.feedback,
      passed,
      timeSpent,
      interactions: inputInteractions
    });

    setIsSubmitted(true);
    setIsCorrect(passed);
    
    onComplete(passed, finalScore);

    if (passed) {
      toast.success(`Excellent engineering work! Score: ${finalScore}%`);
    } else {
      toast.error(`Score: ${finalScore}%. Review the feedback and try again.`);
    }
  };

  const getExerciseIcon = () => {
    switch (exerciseType) {
      case 'circuit-design': return <BoltIcon className="h-6 w-6" />;
      case 'algorithm-implementation': return <CodeBracketIcon className="h-6 w-6" />;
      case 'system-architecture': return <RectangleGroupIcon className="h-6 w-6" />;
      case 'code-debugging': return <WrenchScrewdriverIcon className="h-6 w-6" />;
      case 'technical-specification': return <DocumentTextIcon className="h-6 w-6" />;
      default: return <CpuChipIcon className="h-6 w-6" />;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderExerciseContent = () => {
    switch (exerciseType) {
      case 'circuit-design':
        return renderCircuitDesign();
      case 'algorithm-implementation':
        return renderAlgorithmImplementation();
      case 'system-architecture':
        return renderSystemArchitecture();
      case 'code-debugging':
        return renderCodeDebugging();
      case 'technical-specification':
        return renderTechnicalSpecification();
      default:
        return (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h4 className="font-semibold text-red-800 mb-2">❌ Unknown Exercise Type</h4>
            <p className="text-red-700 mb-3">
              Exercise type "{exerciseType}" is not recognized. Please check the configuration.
            </p>
            <div className="bg-red-100 p-3 rounded text-sm">
              <strong>Supported exercise types:</strong>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>circuit-design</li>
                <li>algorithm-implementation</li>
                <li>system-architecture</li>
                <li>code-debugging</li>
                <li>technical-specification</li>
              </ul>
              <p className="mt-2">
                <strong>Current value:</strong> "{exerciseType}"
              </p>
            </div>
          </div>
        );
    }
  };

  const renderCircuitDesign = () => (
    <div className="space-y-6">
      {scenario?.circuitDescription && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Circuit Requirements:</h4>
          <div className="text-sm text-gray-700">{scenario.circuitDescription}</div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Component List
        </label>
        {[0, 1, 2, 3, 4].map((index) => (
          <div key={index} className="flex space-x-2 mb-2">
            <input
              type="text"
              value={inputs.components?.[index]?.name || ''}
              onChange={(e) => {
                const newComponents = [...(inputs.components || [])];
                newComponents[index] = { 
                  ...newComponents[index], 
                  name: e.target.value 
                };
                handleInputChange('components', newComponents);
              }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
              placeholder={`Component ${index + 1} (e.g., Resistor, LED, Capacitor)`}
              disabled={isSubmitted}
            />
            <input
              type="text"
              value={inputs.components?.[index]?.value || ''}
              onChange={(e) => {
                const newComponents = [...(inputs.components || [])];
                newComponents[index] = { 
                  ...newComponents[index], 
                  value: e.target.value 
                };
                handleInputChange('components', newComponents);
              }}
              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
              placeholder="Value"
              disabled={isSubmitted}
            />
          </div>
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Circuit Calculations
        </label>
        <textarea
          value={inputs.calculations || ''}
          onChange={(e) => handleInputChange('calculations', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 font-mono"
          rows={4}
          placeholder="Show your calculations using Ohm's law (V=IR), power (P=VI), etc."
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Expected Behavior *
        </label>
        <textarea
          value={inputs.expectedBehavior || ''}
          onChange={(e) => handleInputChange('expectedBehavior', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
          rows={3}
          placeholder="Describe how your circuit should behave when powered"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Test Procedure
        </label>
        <textarea
          value={inputs.testProcedure || ''}
          onChange={(e) => handleInputChange('testProcedure', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
          rows={3}
          placeholder="How would you test your circuit to verify it works correctly?"
          disabled={isSubmitted}
        />
      </div>

      {/* Circuit Simulation */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-medium">Circuit Simulator</h4>
            <p className="text-sm text-orange-100">Test your circuit design</p>
          </div>
          <button
            onClick={simulateCircuit}
            disabled={isSubmitted}
            className="px-4 py-2 bg-white text-orange-600 rounded-lg font-medium hover:bg-gray-100"
          >
            Simulate
          </button>
        </div>
        
        {circuitSimulation && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="font-medium">Voltage</div>
              <div>{circuitSimulation.voltage}V</div>
            </div>
            <div>
              <div className="font-medium">Current</div>
              <div>{circuitSimulation.current}A</div>
            </div>
            <div>
              <div className="font-medium">Power</div>
              <div>{circuitSimulation.power}W</div>
            </div>
            <div>
              <div className="font-medium">Status</div>
              <div className={circuitSimulation.status.includes('Warning') ? 'text-yellow-300' : 'text-green-300'}>
                {circuitSimulation.status}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderAlgorithmImplementation = () => (
    <div className="space-y-6">
      {scenario?.problemDescription && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Problem:</h4>
          <div className="text-sm text-gray-700">{scenario.problemDescription}</div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Pseudocode
        </label>
        <textarea
          value={inputs.pseudocode || ''}
          onChange={(e) => handleInputChange('pseudocode', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 font-mono"
          rows={4}
          placeholder="Write pseudocode for your algorithm"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Implementation *
        </label>
        <textarea
          value={inputs.implementation || ''}
          onChange={(e) => handleInputChange('implementation', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 font-mono"
          rows={6}
          placeholder="Implement your algorithm in your preferred programming language"
          disabled={isSubmitted}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Time Complexity *
          </label>
          <input
            type="text"
            value={inputs.timeComplexity || ''}
            onChange={(e) => handleInputChange('timeComplexity', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., O(n log n)"
            disabled={isSubmitted}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Space Complexity
          </label>
          <input
            type="text"
            value={inputs.spaceComplexity || ''}
            onChange={(e) => handleInputChange('spaceComplexity', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., O(1)"
            disabled={isSubmitted}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Test Cases
        </label>
        {[0, 1, 2].map((index) => (
          <div key={index} className="grid md:grid-cols-2 gap-4 mb-2">
            <input
              type="text"
              value={inputs.testCases?.[index]?.input || ''}
              onChange={(e) => {
                const newTestCases = [...(inputs.testCases || [])];
                newTestCases[index] = { 
                  ...newTestCases[index], 
                  input: e.target.value 
                };
                handleInputChange('testCases', newTestCases);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder={`Test ${index + 1} Input`}
              disabled={isSubmitted}
            />
            <input
              type="text"
              value={inputs.testCases?.[index]?.expectedOutput || ''}
              onChange={(e) => {
                const newTestCases = [...(inputs.testCases || [])];
                newTestCases[index] = { 
                  ...newTestCases[index], 
                  expectedOutput: e.target.value 
                };
                handleInputChange('testCases', newTestCases);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder={`Expected Output`}
              disabled={isSubmitted}
            />
          </div>
        ))}
      </div>

      {/* Code Testing */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-4 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-medium">Code Tester</h4>
            <p className="text-sm text-blue-100">Test your algorithm implementation</p>
          </div>
          <button
            onClick={runCodeTest}
            disabled={isSubmitted}
            className="px-4 py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-gray-100"
          >
            Run Tests
          </button>
        </div>
        
        {codeOutput && (
          <pre className="bg-black bg-opacity-30 p-3 rounded text-sm overflow-x-auto">
            {codeOutput}
          </pre>
        )}
      </div>
    </div>
  );

  const renderSystemArchitecture = () => (
    <div className="space-y-6">
      {scenario?.systemRequirements && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">System Requirements:</h4>
          <div className="text-sm text-gray-700">{scenario.systemRequirements}</div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          System Components
        </label>
        {[0, 1, 2, 3].map((index) => (
          <input
            key={index}
            type="text"
            value={inputs.components?.[index] || ''}
            onChange={(e) => {
              const newComponents = [...(inputs.components || [])];
              newComponents[index] = e.target.value;
              handleInputChange('components', newComponents);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 mb-2"
            placeholder={`Component ${index + 1} (e.g., Database, API Server, Load Balancer)`}
            disabled={isSubmitted}
          />
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Data Flow *
        </label>
        <textarea
          value={inputs.dataFlow || ''}
          onChange={(e) => handleInputChange('dataFlow', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
          rows={4}
          placeholder="Describe how data flows through your system components"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Scalability Considerations
        </label>
        <textarea
          value={inputs.scalability || ''}
          onChange={(e) => handleInputChange('scalability', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
          rows={3}
          placeholder="How will your system handle increased load and growth?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Security Considerations
        </label>
        <textarea
          value={inputs.securityConsiderations || ''}
          onChange={(e) => handleInputChange('securityConsiderations', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
          rows={3}
          placeholder="What security measures will you implement?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          System Documentation *
        </label>
        <textarea
          value={inputs.documentation || ''}
          onChange={(e) => handleInputChange('documentation', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
          rows={4}
          placeholder="Provide technical documentation for your system architecture"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  const renderCodeDebugging = () => (
    <div className="space-y-6">
      {scenario?.buggyCode && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Buggy Code:</h4>
          <pre className="bg-black text-green-400 p-3 rounded text-sm overflow-x-auto">
            {scenario.buggyCode}
          </pre>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Bug Identification
        </label>
        {[0, 1, 2].map((index) => (
          <textarea
            key={index}
            value={inputs.bugIdentification?.[index] || ''}
            onChange={(e) => {
              const newBugs = [...(inputs.bugIdentification || [])];
              newBugs[index] = e.target.value;
              handleInputChange('bugIdentification', newBugs);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 mb-2"
            rows={2}
            placeholder={`Bug ${index + 1}: Describe the issue and its location`}
            disabled={isSubmitted}
          />
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Proposed Fixes
        </label>
        {[0, 1, 2].map((index) => (
          <textarea
            key={index}
            value={inputs.fixes?.[index] || ''}
            onChange={(e) => {
              const newFixes = [...(inputs.fixes || [])];
              newFixes[index] = e.target.value;
              handleInputChange('fixes', newFixes);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 mb-2"
            rows={2}
            placeholder={`Fix ${index + 1}: How would you resolve this bug?`}
            disabled={isSubmitted}
          />
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Debugging Process Explanation *
        </label>
        <textarea
          value={inputs.explanation || ''}
          onChange={(e) => handleInputChange('explanation', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
          rows={3}
          placeholder="Explain your debugging methodology and reasoning"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Corrected Code *
        </label>
        <textarea
          value={inputs.improvedCode || ''}
          onChange={(e) => handleInputChange('improvedCode', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 font-mono"
          rows={6}
          placeholder="Provide the corrected version of the code"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  const renderTechnicalSpecification = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Functional Requirements
        </label>
        {[0, 1, 2].map((index) => (
          <textarea
            key={index}
            value={inputs.requirements?.[index] || ''}
            onChange={(e) => {
              const newReqs = [...(inputs.requirements || [''])];
              newReqs[index] = e.target.value;
              handleInputChange('requirements', newReqs);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 mb-2"
            rows={2}
            placeholder={`Requirement ${index + 1}: What must the system do?`}
            disabled={isSubmitted}
          />
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Constraints
        </label>
        {[0, 1, 2].map((index) => (
          <input
            key={index}
            type="text"
            value={inputs.constraints?.[index] || ''}
            onChange={(e) => {
              const newConstraints = [...(inputs.constraints || [''])];
              newConstraints[index] = e.target.value;
              handleInputChange('constraints', newConstraints);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 mb-2"
            placeholder={`Constraint ${index + 1}: Budget, time, technology limitations`}
            disabled={isSubmitted}
          />
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Technical Specifications *
        </label>
        <textarea
          value={inputs.specifications || ''}
          onChange={(e) => handleInputChange('specifications', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
          rows={6}
          placeholder="Detailed technical specifications including architecture, technologies, interfaces, and performance requirements"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Acceptance Criteria
        </label>
        {[0, 1, 2].map((index) => (
          <textarea
            key={index}
            value={inputs.acceptance?.[index] || ''}
            onChange={(e) => {
              const newAcceptance = [...(inputs.acceptance || [''])];
              newAcceptance[index] = e.target.value;
              handleInputChange('acceptance', newAcceptance);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 mb-2"
            rows={2}
            placeholder={`Acceptance Criterion ${index + 1}: How will you know it's done?`}
            disabled={isSubmitted}
          />
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Implementation Timeline
        </label>
        <textarea
          value={inputs.timeline || ''}
          onChange={(e) => handleInputChange('timeline', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
          rows={3}
          placeholder="Estimated timeline for development phases and milestones"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getExerciseIcon()}
            <div className="ml-3">
              <h3 className="text-xl font-bold">{content.title}</h3>
              <p className="text-orange-100 text-sm">{content.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Focus indicator */}
            <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
              windowFocused ? 'bg-green-500 bg-opacity-20 text-green-100' : 'bg-red-500 bg-opacity-20 text-red-100'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-1 ${
                windowFocused ? 'bg-green-300' : 'bg-red-300'
              }`}></div>
              {windowFocused ? 'Focused' : 'Not Focused'}
            </div>
            
            <div className="text-sm">
              Time: {formatTime(timeSpent)}
            </div>
            
            {isEngaged && (
              <div className="flex items-center text-xs px-2 py-1 rounded-full bg-green-500 bg-opacity-20 text-green-100">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Engaged
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <p className="text-gray-700">{instructions}</p>
        
        {/* Engagement progress */}
        {!isEngaged && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>Minimum development time</span>
              <span>{formatTime(timeSpent)} / {formatTime(minimumEngagementTime)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((timeSpent / minimumEngagementTime) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Exercise Content */}
      <div className="p-6">
        {renderExerciseContent()}

        {/* Hints Section */}
        {hints && hints.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowHints(!showHints)}
              className="flex items-center text-orange-600 hover:text-orange-700 mb-3"
            >
              <LightBulbIcon className="h-4 w-4 mr-2" />
              {showHints ? 'Hide Hints' : 'Show Hints'}
            </button>
            
            {showHints && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800">
                    Hint {currentHint + 1} of {hints.length}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentHint(Math.max(0, currentHint - 1))}
                      disabled={currentHint === 0}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentHint(Math.min(hints.length - 1, currentHint + 1))}
                      disabled={currentHint === hints.length - 1}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
                <p className="text-yellow-700 text-sm">{hints[currentHint]}</p>
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-6">
          {!isSubmitted ? (
            <button
              onClick={handleSubmit}
              className="bg-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-700 transition-colors"
            >
              Submit Solution
            </button>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                {isCorrect ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-600 mr-2" />
                )}
                <span className="font-medium">
                  Score: {results.score}% {isCorrect ? '(Passed)' : '(Review Required)'}
                </span>
              </div>
              <div className="text-sm text-gray-600 whitespace-pre-line">
                {results.feedback}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Time spent: {formatTime(results.timeSpent)} | Interactions: {results.interactions}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EngineeringTechInteractive;